import { Fragment, createContext, useContext, useMemo, useState, type MouseEvent, type ReactNode } from "react";
import {
  columnPinningFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createColumnHelper,
  rowSelectionFeature,
  rowSortingFeature,
  tableFeatures,
  useTable,
  type Cell,
  type CellContext,
  type ColumnDef,
  type Header,
  type HeaderContext,
  type Row,
  type RowData,
  type TableState,
  FlexRender,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { cx } from "../../cx";
import { SkeletonRows } from "../../Skeleton";
import { Grid, GridCell, GridEmptyRow, GridHeadCell, GridRow, GridWrap } from "../../Table";
import { GroupHeader } from "../GroupHeader";
import { ResizeHandle } from "./ResizeHandle";
import { parseSort, type SortParam } from "./sort";
import { useRowKeyboardNav } from "./keyboard";

export interface DataColumnMeta {
  readonly label?: string;
  readonly sort?: string;
  readonly width?: number;
  readonly cellClassName?: string;
  readonly interactive?: boolean;
  readonly stickyCell?: boolean;
}

const COLUMN_META: DataColumnMeta = {};

export const dataTableFeatures = tableFeatures({
  columnVisibilityFeature,
  columnSizingFeature,
  columnPinningFeature,
  rowSelectionFeature,
  rowSortingFeature,
  columnMeta: COLUMN_META,
});

type Features = typeof dataTableFeatures;
export type DataColumn<T extends RowData> = ColumnDef<Features, T>;

export function dataColumns<T extends RowData>() {
  return createColumnHelper<Features, T>();
}

export const SELECT_COLUMN = "select";
export const VIRTUAL_THRESHOLD = 100;
const MIN_WIDTH = 48;
const ROW_HEIGHT = 40;

export interface RowOptions {
  readonly label?: string;
  readonly muted?: boolean;
  readonly className?: string;
  readonly onOpen?: () => void;
  readonly onActivate?: () => void;
  readonly titles?: Readonly<Record<string, string | undefined>>;
  readonly onPick?: () => void;
  readonly ariaSelected?: boolean;
  readonly before?: ReactNode;
}

export interface DataGroup<T extends RowData> {
  readonly key: string;
  readonly label: string;
  readonly count: number;
  readonly note: string;
  readonly rows: readonly T[];
  readonly collapsed: boolean;
  readonly attention?: boolean;
  readonly onToggle: () => void;
}

export interface DataSelection<T extends RowData> {
  readonly picked: ReadonlySet<string>;
  readonly onToggle: (row: T) => void;
  readonly onToggleLoaded: () => void;
  readonly rowLabel: (row: T) => string;
  readonly canSelect?: (row: T) => boolean;
}

export interface DataTableProps<T extends RowData> {
  label: string;
  columns: readonly DataColumn<T>[];
  rows: readonly T[];
  rowId: (row: T) => string;
  minWidth: number;
  rowCount?: number | undefined;
  pinned?: readonly string[];
  hidden?: ReadonlySet<string>;
  sort?: SortParam;
  selection?: DataSelection<T>;
  groups?: readonly DataGroup<T>[] | null;
  selectedId?: string | null;
  rowOptions?: (row: T) => RowOptions;
  isPending?: boolean;
  skeletonRows?: number;
  busy?: boolean;
  empty?: ReactNode;
  emptyRow?: ReactNode;
  virtual?: boolean;
  wrap?: boolean;
  wrapClassName?: string;
  tableClassName?: string;
  after?: ReactNode;
  bodyAfter?: ReactNode;
  widthsReset?: number;
}

function useColumnSizing(widthsReset: number | undefined) {
  const [columnSizing, setColumnSizing] = useState<Record<string, number>>({});
  const [sizedFor, setSizedFor] = useState(widthsReset);
  if (sizedFor !== widthsReset) {
    setSizedFor(widthsReset);
    setColumnSizing({});
  }
  return [columnSizing, setColumnSizing] as const;
}

type Entry<T extends RowData> = { kind: "group"; group: DataGroup<T> } | { kind: "row"; row: Row<Features, T> };

const NO_HIDDEN: ReadonlySet<string> = new Set();

export function DataTable<T extends RowData>({
  label,
  columns,
  rows,
  rowId,
  minWidth,
  rowCount,
  pinned = [],
  hidden = NO_HIDDEN,
  sort,
  selection,
  groups = null,
  selectedId = null,
  rowOptions,
  isPending = false,
  skeletonRows,
  busy,
  empty,
  emptyRow,
  virtual = true,
  wrap = true,
  wrapClassName,
  tableClassName,
  after,
  bodyAfter,
  widthsReset,
}: DataTableProps<T>) {
  const [columnSizing, setColumnSizing] = useColumnSizing(widthsReset);
  const selectable = selection !== undefined;
  const allColumns = useMemo(
    () => (selectable ? [selectColumn<T>(), ...columns] : [...columns]),
    [selectable, columns],
  );
  const data = useMemo(() => [...rows], [rows]);
  const labelOnly = useMemo(
    () => new Set(allColumns.flatMap((c) => (c.header === undefined && c.id ? [c.id] : []))),
    [allColumns],
  );
  const table = useTable({
    features: dataTableFeatures,
    columns: allColumns,
    data,
    getRowId: (row) => rowId(row),
    manualSorting: true,
    sortDescFirst: true,
    enableMultiSort: false,
    state: { ...tableState(allColumns, hidden, pinned, selection, sort), columnSizing },
    onColumnSizingChange: setColumnSizing,
    onSortingChange: () => undefined,
  });
  const headers = table.getHeaderGroups()[0]?.headers ?? [];
  const visibleCount = headers.length;
  const tableRows = table.getRowModel().rows;
  const entries = useMemo(() => entriesOf(tableRows, groups, rowId), [tableRows, groups, rowId]);
  const layout = layoutOf(
    headers.map((h) => h.column),
    columnSizing,
  );
  const resized = Object.keys(columnSizing).length > 0;
  const width = resized ? minWidth + resizeDelta(headers, columnSizing) : minWidth;
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
  const body = (
    <Grid minWidth={width} aria-label={label} aria-rowcount={rowCount} className={tableClassName}>
      <thead>
        <tr>
          {headers.map((header) => (
            <HeadCell
              key={header.id}
              header={header}
              sort={sort}
              layout={layout.get(header.column.id) ?? FLUID}
              labelOnly={labelOnly.has(header.column.id)}
            />
          ))}
        </tr>
      </thead>
      <tbody>
        {isPending ? (
          <SkeletonRows columns={visibleCount} {...(skeletonRows === undefined ? {} : { rows: skeletonRows })} />
        ) : null}
        {!isPending && rows.length === 0
          ? (emptyRow ?? (empty ? <GridEmptyRow columns={visibleCount}>{empty}</GridEmptyRow> : null))
          : null}
        <Body
          entries={entries}
          columns={visibleCount}
          virtual={virtual && entries.length > VIRTUAL_THRESHOLD}
          scrollElement={scrollElement}
          selectedId={selectedId}
          rowOptions={rowOptions}
          selection={selection}
          layout={layout}
        />
        {bodyAfter}
      </tbody>
    </Grid>
  );
  const content = <SelectionContext value={selectionHandlers(selection, rows, rowId)}>{body}</SelectionContext>;
  if (!wrap) return content;
  return (
    <GridWrap ref={setScrollElement} className={wrapClassName} aria-busy={busy || undefined}>
      {content}
      {after}
    </GridWrap>
  );
}

interface SelectionHandlers {
  readonly toggle: (id: string) => void;
  readonly toggleLoaded: () => void;
  readonly label: (id: string) => string;
  readonly selectable: (id: string) => boolean;
}

const SelectionContext = createContext<SelectionHandlers | null>(null);

function selectionHandlers<T extends RowData>(
  selection: DataSelection<T> | undefined,
  rows: readonly T[],
  rowId: (row: T) => string,
): SelectionHandlers | null {
  if (!selection) return null;
  const byId = new Map(rows.map((r) => [rowId(r), r]));
  return {
    toggle: (id) => {
      const row = byId.get(id);
      if (row) selection.onToggle(row);
    },
    toggleLoaded: selection.onToggleLoaded,
    label: (id) => {
      const row = byId.get(id);
      return row ? selection.rowLabel(row) : id;
    },
    selectable: (id) => {
      const row = byId.get(id);
      return row !== undefined && (selection.canSelect?.(row) ?? true);
    },
  };
}

function SelectLoaded<T extends RowData>({ table }: HeaderContext<Features, T>) {
  const selection = useContext(SelectionContext);
  const rows = table.getRowModel().rows.filter((r) => selection?.selectable(r.id) ?? true);
  const all = rows.length > 0 && rows.every((r) => r.getIsSelected());
  const some = rows.some((r) => r.getIsSelected());
  return (
    <input
      type="checkbox"
      aria-label={all ? "Clear the selection of loaded rows" : "Select the loaded rows"}
      className="size-3.5 accent-link"
      checked={all}
      ref={(el) => {
        if (el) el.indeterminate = some && !all;
      }}
      onChange={selection?.toggleLoaded}
    />
  );
}

function SelectRow<T extends RowData>({ row }: CellContext<Features, T>) {
  const selection = useContext(SelectionContext);
  if (selection && !selection.selectable(row.id)) return null;
  return (
    <input
      type="checkbox"
      aria-label={`Select ${selection?.label(row.id) ?? row.id}`}
      className="size-3.5 accent-link"
      checked={row.getIsSelected()}
      onChange={() => {
        selection?.toggle(row.id);
      }}
    />
  );
}

// FlexRender mounts header and cell functions as components: inline closures would remount the checkboxes on every
// selection change and drop their focus, so these stay module-level and read the handlers from context.
function selectColumn<T extends RowData>(): DataColumn<T> {
  return {
    id: SELECT_COLUMN,
    meta: { label: "Select", width: 36, interactive: true, cellClassName: "px-0 text-center shadow-none" },
    header: SelectLoaded,
    cell: SelectRow,
  };
}

function tableState<T extends RowData>(
  columns: readonly DataColumn<T>[],
  hidden: ReadonlySet<string>,
  pinned: readonly string[],
  selection: DataSelection<T> | undefined,
  sort: SortParam | undefined,
): Partial<TableState<Features>> {
  const current = parseSort(sort?.value);
  return {
    columnVisibility: Object.fromEntries([...hidden].map((id) => [id, false])),
    columnPinning: { start: selection ? [SELECT_COLUMN, ...pinned] : [...pinned], end: [] },
    rowSelection: selection ? Object.fromEntries([...selection.picked].map((id) => [id, true])) : {},
    sorting: current ? [{ id: columnOfSort(columns, current.key), desc: current.descending }] : [],
  };
}

function columnOfSort<T extends RowData>(columns: readonly DataColumn<T>[], key: string): string {
  return columns.find((c) => c.meta?.sort === key)?.id ?? key;
}

function resizeDelta<T extends RowData>(
  headers: readonly Header<Features, T>[],
  sizing: Record<string, number>,
): number {
  return headers.reduce((sum, h) => {
    const set = sizing[h.column.id];
    const base = h.column.columnDef.meta?.width;
    return set === undefined || base === undefined ? sum : sum + set - base;
  }, 0);
}

function entriesOf<T extends RowData>(
  rows: readonly Row<Features, T>[],
  groups: readonly DataGroup<T>[] | null,
  rowId: (row: T) => string,
): Entry<T>[] {
  if (!groups) return rows.map((row) => ({ kind: "row", row }));
  const byId = new Map(rows.map((r) => [r.id, r]));
  return groups.flatMap((group): Entry<T>[] => [
    { kind: "group", group },
    ...(group.collapsed
      ? []
      : group.rows.flatMap((r): Entry<T>[] => {
          const row = byId.get(rowId(r));
          return row ? [{ kind: "row", row }] : [];
        })),
  ]);
}

interface ColumnLayout {
  readonly width: number | undefined;
  readonly sticky: boolean;
  readonly left?: number;
}

type Layout = ReadonlyMap<string, ColumnLayout>;

function layoutOf<T extends RowData>(
  columns: readonly Header<Features, T>["column"][],
  sizing: Record<string, number>,
): Layout {
  const layout = new Map<string, ColumnLayout>();
  let left = 0;
  for (const column of columns) {
    const width = sizing[column.id] ?? column.columnDef.meta?.width;
    if (column.getIsPinned() !== "start") {
      layout.set(column.id, { width, sticky: false });
      continue;
    }
    layout.set(column.id, left === 0 ? { width, sticky: true } : { width, sticky: true, left });
    left += width ?? 0;
  }
  return layout;
}

const FLUID: ColumnLayout = { width: undefined, sticky: false };

function HeadCell<T extends RowData>({
  header,
  sort,
  layout,
  labelOnly,
}: {
  header: Header<Features, T>;
  sort: SortParam | undefined;
  layout: ColumnLayout;
  labelOnly: boolean;
}) {
  const column = header.column;
  const meta = column.columnDef.meta;
  const key = meta?.sort;
  const { width } = layout;
  return (
    <GridHeadCell
      width={width}
      sticky={layout.sticky}
      left={layout.left}
      label={meta?.label}
      {...(key && sort
        ? {
            sort: sort.directionOf(key),
            onSort: () => {
              sort.cycle(key);
            },
          }
        : {})}
      resize={
        column.id === SELECT_COLUMN || width === undefined ? null : (
          <ResizeHandle
            label={meta?.label ?? column.id}
            width={width}
            onResize={(next) => {
              column.table.setColumnSizing((s) => ({ ...s, [column.id]: Math.max(MIN_WIDTH, Math.round(next)) }));
            }}
            onReset={() => {
              column.resetSize();
            }}
          />
        )
      }
    >
      {header.isPlaceholder || labelOnly ? null : <FlexRender header={header} />}
    </GridHeadCell>
  );
}

function Body<T extends RowData>({
  entries,
  columns,
  virtual,
  scrollElement,
  selectedId,
  rowOptions,
  selection,
  layout,
}: {
  entries: readonly Entry<T>[];
  columns: number;
  virtual: boolean;
  scrollElement: HTMLDivElement | null;
  selectedId: string | null;
  rowOptions: ((row: T) => RowOptions) | undefined;
  selection: DataSelection<T> | undefined;
  layout: Layout;
}) {
  const virtualizer = useVirtualizer({
    count: entries.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => ROW_HEIGHT,
    overscan: 12,
    enabled: virtual,
  });
  const renderRow = (row: Row<Features, T>) => {
    const options = rowOptions?.(row.original) ?? {};
    const pick = selection
      ? () => {
          if (selection.canSelect?.(row.original) ?? true) selection.onToggle(row.original);
        }
      : options.onPick;
    const element = (
      <DataRow
        key={row.id}
        row={row}
        selected={row.id === selectedId}
        options={options}
        layout={layout}
        onPick={pick}
      />
    );
    return options.before ? (
      <Fragment key={row.id}>
        {options.before}
        {element}
      </Fragment>
    ) : (
      element
    );
  };
  const render = (entry: Entry<T>) =>
    entry.kind === "group" ? (
      <GroupHeader
        key={`group-${entry.group.key}`}
        colSpan={columns}
        label={entry.group.label}
        count={entry.group.count}
        note={entry.group.note}
        collapsed={entry.group.collapsed}
        attention={entry.group.attention ?? false}
        onToggle={entry.group.onToggle}
      />
    ) : (
      renderRow(entry.row)
    );
  if (!virtual) return <>{entries.map(render)}</>;
  const items = virtualizer.getVirtualItems();
  const top = items[0]?.start ?? 0;
  const bottom = virtualizer.getTotalSize() - (items.at(-1)?.end ?? 0);
  return (
    <>
      {top > 0 ? <Spacer columns={columns} height={top} /> : null}
      {items.map((item) => {
        const entry = entries[item.index];
        return entry ? <Fragment key={item.key}>{render(entry)}</Fragment> : null;
      })}
      {bottom > 0 ? <Spacer columns={columns} height={bottom} /> : null}
    </>
  );
}

function Spacer({ columns, height }: { columns: number; height: number }) {
  return (
    <tr aria-hidden="true">
      <td colSpan={columns} style={{ height }} className="border-0 p-0" />
    </tr>
  );
}

function DataRow<T extends RowData>({
  row,
  selected,
  options,
  onPick,
  layout,
}: {
  row: Row<Features, T>;
  selected: boolean;
  options: RowOptions;
  layout: Layout;
  onPick: (() => void) | undefined;
}) {
  const onKeyDown = useRowKeyboardNav({ onActivate: options.onActivate ?? options.onOpen, onPick });
  const interactive = options.onOpen !== undefined || options.onActivate !== undefined;
  return (
    <GridRow
      data-row
      selected={selected}
      muted={options.muted ?? false}
      tabIndex={interactive ? 0 : undefined}
      aria-label={options.label}
      aria-selected={options.ariaSelected}
      className={cx(interactive && "cursor-pointer", options.className)}
      onClick={options.onOpen}
      onDoubleClick={options.onActivate}
      onKeyDown={interactive || onPick ? onKeyDown : undefined}
    >
      {row.getVisibleCells().map((cell) => (
        <DataCell
          key={cell.id}
          cell={cell}
          title={options.titles?.[cell.column.id]}
          layout={layout.get(cell.column.id) ?? FLUID}
        />
      ))}
    </GridRow>
  );
}

function stop(event: MouseEvent) {
  event.stopPropagation();
}

function DataCell<T extends RowData>({
  cell,
  title,
  layout,
}: {
  cell: Cell<Features, T>;
  title: string | undefined;
  layout: ColumnLayout;
}) {
  const meta = cell.column.columnDef.meta;
  return (
    <GridCell
      sticky={layout.sticky || meta?.stickyCell === true}
      style={layout.left === undefined ? undefined : { left: layout.left }}
      title={title}
      className={meta?.cellClassName}
      onClick={meta?.interactive ? stop : undefined}
    >
      <FlexRender cell={cell} />
    </GridCell>
  );
}
